import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { sayAlex } from '@/audio/alex';
import { confirmLine, RETRY_LINE } from '@/speech/control/conversation';
import { CONFIRM_AT, YES_NO } from '@/speech/control/decide';
import { isQuestion, keywordTopic, matchChoice, type Choice } from '@/speech/understand/match';
import { TOPICS } from '@/speech/understand/topics';
import { holoFrame } from '@/ui/holo';
import { TongueDrag } from '@/ui/tongue3d/TongueDrag';
import type { DragZone } from '@/ui/tongue3d/dragZone';
import { blendShapes, shapeOf, TongueScene } from '@/ui/tongue3d/TongueScene';
import { BUILD, deal, FIX, SPIN, tallyLine, type Ask, type Show, type Tally } from './exercises';
import { ACTIVITY_NAMES, LOOKALIKES, offers, WATCH, type Activity } from './lessons';
import { choosePrompt, NEXT_PROMPT, QUIZ_PROMPT, rulePick, type LabPrompt } from './prompts';
import { useVoice } from './useVoice';
import styles from './TongueLab.module.css';

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/** Whether the 3D stage is Alex's hologram projector (in his lab) rather than a plain panel. */
const Holo = createContext(false);

/** Where an activity draws the 3D mouth: a panel, or in Alex's lab his hologram projector. */
function Stage({ children }: { children: ReactNode }) {
  const holo = useContext(Holo);
  const frame = holoFrame(true);
  return (
    <div
      className={
        holo ? `${styles.stage ?? ''} ${styles.holoStage ?? ''} ${frame.className}` : styles.stage
      }
      style={holo ? frame.style : undefined}
    >
      {children}
    </div>
  );
}

/** Takes what the child said; true when the activity used it. */
type Hears = (text: string) => boolean;
/**
 * What an activity listens for: its handler, and the question it has open (for the language
 * model, when the rules cannot place an answer), if any.
 */
interface Listening {
  hears: Hears;
  prompt: () => LabPrompt | null;
}
/** An activity tells the lab what it listens for (null: nothing right now). */
type Register = (listening: Listening | null) => void;
type Done = (tally: Tally) => void;

const NEXT: Choice[] = [{ id: 'next', says: ['next'] }];

/** Lets the lab hand an activity what the child said, always to the activity's latest handler. */
function useHears(register: Register, hears: Hears, prompt: LabPrompt | null = null) {
  const latest = useRef({ hears, prompt });
  useEffect(() => {
    latest.current = { hears, prompt };
  });
  useEffect(() => {
    register({
      hears: (text) => latest.current.hears(text),
      prompt: () => latest.current.prompt,
    });
    return () => {
      register(null);
    };
  }, [register]);
}

/**
 * The Tongue Lab (issue #25): the 3D mouth as short activities, a different one each visit, and
 * the child picks what comes next. Every answer can be spoken (microphone on) or tapped; the
 * child can also ask the helper a question at any time, answered from the approved topics.
 * Watch: relaxed to a strong r one part at a time, the camera turning to show each part. Build:
 * the child decides each part of the r and the tongue does it. Which is r?: two shapes in turn,
 * the r and a look-alike. Spot the mistake: an r with one part wrong. Move the tongue: drag the
 * middle up to close under the roof without touching. Spin and look: questions while the camera
 * turns. `onDone` reports each finished activity and whether its answers were right first time.
 */
export function TongueLab({
  activity: first,
  onDone,
  onStart,
  holo = false,
}: {
  activity: Activity;
  /** In Alex's lab: the mouth floats over his hologram projector, beside the questions. */
  holo?: boolean;
  onDone?: (activity: Activity, ok: boolean) => void;
  /** The child picked another activity at the end (not called for the first). */
  onStart?: (activity: Activity) => void;
}) {
  const [activity, setActivity] = useState(first);
  const [round, setRound] = useState(0);
  const [finished, setFinished] = useState<Tally | null>(null);
  const [helper, setHelper] = useState('');
  const [asking, setAsking] = useState(false);
  const listening = useRef<Listening | null>(null);
  const register = useCallback<Register>((l) => {
    listening.current = l;
  }, []);

  const choose = (next: Activity) => {
    onStart?.(next);
    setActivity(next);
    setRound((r) => r + 1);
    setFinished(null);
    setHelper('');
  };

  const finish = (tally: Tally) => {
    setFinished(tally);
    onDone?.(activity, tally.firstTry === tally.asked);
  };

  const [a, b] = offers(activity);
  /** The classifier's reading of the last answer, waiting for the child's yes (a check-back). */
  const checking = useRef<string | null>(null);
  const voice = useVoice(async (text, understand) => {
    // The open question: what the activity (or the finish screen's choice) is asking.
    const current: Listening | null = finished
      ? {
          hears: (t) => {
            const pick = rulePick(choosePrompt(activity), t);
            if (pick) choose(pick as Activity);
            return pick !== null;
          },
          prompt: () => choosePrompt(activity),
        }
      : listening.current;
    // A check-back is open: yes takes the classifier's reading, no clears it.
    const pending = checking.current;
    checking.current = null;
    if (pending) {
      const yesNo = matchChoice(text, YES_NO.options);
      if (yesNo === 'yes' && current?.hears(pending)) {
        setHelper('');
        return;
      }
      if (yesNo === 'no') {
        setHelper('Okay. Say it again, or tap your answer.');
        void sayAlex(RETRY_LINE);
        return;
      }
    }
    if (current?.hears(text)) {
      setHelper('');
      return;
    }
    // The rules could not place it: the classifier reads it against the open question's options.
    // Its reading is checked back, never acted on: here a misreading would mark the child wrong
    // (voice-control.md).
    const prompt = current?.prompt();
    if (prompt && !isQuestion(text)) {
      const read = await understand.choose(
        prompt.question,
        prompt.options.map((o) => o.label),
        text,
      );
      const option = read && read.index !== null ? prompt.options[read.index] : undefined;
      if (option && read && read.p >= CONFIRM_AT) {
        checking.current = option.label;
        setHelper(`Did you mean “${option.label}”? Say yes or no, or tap it.`);
        // Alex asks it aloud (recorded per option: voicePlan.eval.ts).
        void sayAlex(confirmLine(option.label));
        return;
      }
    }
    if (isQuestion(text) || keywordTopic(text)) {
      setHelper('…');
      void understand.answer(text).then((reply) => {
        setHelper(reply.text);
      });
      return;
    }
    setHelper('I did not catch an answer there. You can say it again, or tap.');
  });

  return (
    <Holo.Provider value={holo}>
      <div className={styles.lab} data-look={holo ? 'holo' : undefined}>
        {finished ? (
          <div className={styles.finish}>
            <p className={styles.caption} aria-live="polite">
              {tallyLine(finished)} What would you like next?
            </p>
            <div className={styles.actions}>
              {[a, b].map((next) => (
                <button
                  key={next}
                  type="button"
                  className={styles.button}
                  onClick={() => {
                    choose(next);
                  }}
                >
                  {ACTIVITY_NAMES[next].label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          // Tapping an answer moves on: the helper's last answer goes.
          <div
            className={styles.activity}
            onClickCapture={() => {
              setHelper('');
            }}
          >
            {activity === 'drag' ? (
              <DragActivity key={round} register={register} onDone={finish} />
            ) : activity === 'watch' || activity === 'quiz' ? (
              <SceneActivity key={round} activity={activity} register={register} onDone={finish} />
            ) : (
              <AskActivity key={round} kind={activity} register={register} onDone={finish} />
            )}
          </div>
        )}
        {helper && (
          <p className={styles.helper} aria-live="polite">
            {helper}
          </p>
        )}
        {asking && (
          <div className={styles.chips}>
            {TOPICS.slice(0, 6).map((t) => (
              <button
                key={t.id}
                type="button"
                className={styles.chip}
                onClick={() => {
                  setHelper(t.answer);
                }}
              >
                {QUESTION_LABELS[t.id] ?? t.about}
              </button>
            ))}
          </div>
        )}
        <div className={styles.voice}>
          <button
            type="button"
            className={styles.mic}
            aria-pressed={voice.state.kind !== 'off' && voice.state.kind !== 'error'}
            onClick={() => {
              void (voice.state.kind === 'off' || voice.state.kind === 'error'
                ? voice.start()
                : voice.stop());
            }}
          >
            {micLabel(voice.state)}
          </button>
          <button
            type="button"
            className={styles.ask}
            aria-expanded={asking}
            onClick={() => {
              setAsking((v) => !v);
            }}
          >
            Ask me
          </button>
          {voice.heard && (
            <span className={styles.heard} aria-live="polite">
              I heard: “{voice.heard}”
            </span>
          )}
        </div>
      </div>
    </Holo.Provider>
  );
}

/** Short questions for the "Ask me" buttons (the spoken ones can be phrased any way). */
const QUESTION_LABELS: Readonly<Record<string, string>> = {
  where: 'Where does my tongue go?',
  touch: 'Does it touch the roof?',
  tip: 'Tip up or down?',
  lips: 'What do my lips do?',
  w: 'How is r different from w?',
  hard: 'Why is r so hard?',
};

function micLabel(state: ReturnType<typeof useVoice>['state']): string {
  switch (state.kind) {
    case 'off':
      return '🎤 Answer by voice';
    case 'loading':
      return `Getting ready… ${String(state.percent)}%`;
    case 'listening':
      return '🎤 Listening (tap to stop)';
    case 'thinking':
      return '🎤 …';
    case 'error':
      return '🎤 Microphone off: tap to try again';
  }
}

/** What the dot under the roof means (shown with it). */
function GapKey() {
  return (
    <span className={styles.key}>
      <i className={styles.closest} /> closest to the roof <i className={styles.touching} />{' '}
      touching
    </span>
  );
}

/** The mouth model on a host element, created once. */
function useScene() {
  const host = useRef<HTMLDivElement>(null);
  const scene = useRef<TongueScene | null>(null);
  useEffect(() => {
    if (!host.current) return;
    const s = new TongueScene(host.current);
    scene.current = s;
    s.setBlend('rest', 'rest', 1);
    return () => {
      s.dispose();
      scene.current = null;
    };
  }, []);
  return { host, scene };
}

/** Moves the model to what `show` describes (and turns the camera round when asked). */
async function present(s: TongueScene, show: Show) {
  await s.ready;
  s.setGapMarker(Boolean(show.gap));
  const shot = s.currentShot === show.shot ? Promise.resolve() : s.setShot(show.shot, 1200);
  await Promise.all([s.moveTo(show.shape, 1100), shot]);
  if (show.tour) await s.tour(6500);
}

function AskActivity({
  kind,
  register,
  onDone,
}: {
  kind: 'build' | 'fix' | 'spin';
  register: Register;
  onDone: Done;
}) {
  const { host, scene } = useScene();
  const [items] = useState<Ask[]>(() =>
    kind === 'build' ? [...BUILD] : deal(kind === 'fix' ? FIX : SPIN, 3),
  );
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [tally, setTally] = useState<Tally>({ asked: 0, firstTry: 0 });
  const item = items[index];
  const caption =
    !item || picked === null
      ? (item?.question ?? '')
      : picked === item.answer
        ? item.right
        : item.wrong;

  useEffect(() => {
    const s = scene.current;
    if (!s || !item) return;
    void present(s, item.show);
  }, [item, scene]);

  const answer = (id: string) => {
    const s = scene.current;
    if (!item || picked !== null) return;
    setTally((t) => ({ asked: t.asked + 1, firstTry: t.firstTry + (id === item.answer ? 1 : 0) }));
    setPicked(id);
    if (s && item.after) void present(s, item.after);
  };

  const next = () => {
    if (picked === null) return;
    if (index + 1 >= items.length) {
      onDone(tally);
      return;
    }
    setPicked(null);
    setIndex(index + 1);
  };

  useHears(
    register,
    (text) => {
      if (!item) return false;
      if (picked === null) {
        const id = matchChoice(text, item.options);
        if (id) answer(id);
        return id !== null;
      }
      if (matchChoice(text, NEXT)) {
        next();
        return true;
      }
      return false;
    },
    item && picked === null ? { question: item.question, options: item.options } : null,
  );

  if (!item) return null;
  return (
    <>
      <Stage>
        <div
          ref={host}
          className={styles.model}
          role="img"
          aria-label="The mouth seen through the cheek"
        />
        <span className={styles.progress}>
          {index + 1} / {items.length}
        </span>
        {(picked !== null && item.after ? item.after.gap : item.show.gap) && <GapKey />}
      </Stage>
      <p className={styles.caption} aria-live="polite">
        {caption}
      </p>
      <div className={styles.actions}>
        {picked === null ? (
          item.options.map((o) => (
            <button
              key={o.id}
              type="button"
              className={styles.button}
              onClick={() => {
                answer(o.id);
              }}
            >
              {o.label}
            </button>
          ))
        ) : (
          <button type="button" className={styles.button} onClick={next}>
            {index + 1 >= items.length ? 'Done' : 'Next'}
          </button>
        )}
      </div>
    </>
  );
}

const DRAG_SAY: Record<DragZone, string> = {
  low: 'Lift the middle of the tongue higher.',
  right: 'Just right: close to the roof, not touching!',
  touch: 'Touching the roof: let it down a tiny bit.',
};

function DragActivity({ register, onDone }: { register: Register; onDone: Done }) {
  const [say, setSay] = useState('Drag the blue dot to lift the middle of the tongue.');
  const [done, setDone] = useState(false);
  const tries = useRef(0);
  useHears(register, (text) => {
    if (!done || !matchChoice(text, NEXT)) return false;
    onDone({ asked: 1, firstTry: tries.current <= 1 ? 1 : 0 });
    return true;
  });
  return (
    <>
      <Stage>
        <TongueDrag
          label="The mouth from the side. Drag the middle of the tongue up toward the roof."
          onZone={(zone) => {
            if (!done) setSay(DRAG_SAY[zone]);
          }}
          onDone={(zone) => {
            if (done) return;
            tries.current++;
            if (zone !== 'right') return;
            setDone(true);
            setSay('That is the strong r shape: close to the roof, not touching. Now you try it!');
          }}
        />
      </Stage>
      <p className={styles.caption} aria-live="polite">
        {say}
      </p>
      {done && (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.button}
            onClick={() => {
              onDone({ asked: 1, firstTry: tries.current <= 1 ? 1 : 0 });
            }}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}

interface QuizState {
  /** Which shape is number 1 and number 2. */
  shapes: readonly ['rBunched' | 'w' | 'l', 'rBunched' | 'w' | 'l'];
  lookalike: 'w' | 'l';
  showing: 0 | 1;
  answered: 0 | 1 | null;
}

function newQuiz(): QuizState {
  const lookalike = Math.random() < 0.5 ? 'w' : 'l';
  const shapes =
    Math.random() < 0.5 ? (['rBunched', lookalike] as const) : ([lookalike, 'rBunched'] as const);
  return { shapes, lookalike, showing: 0, answered: null };
}

/** Quiz rounds per activity. */
const QUIZ_ROUNDS = 3;

function SceneActivity({
  activity,
  register,
  onDone,
}: {
  activity: 'watch' | 'quiz';
  register: Register;
  onDone: Done;
}) {
  const { host, scene } = useScene();
  const [caption, setCaption] = useState('');
  const [watching, setWatching] = useState(activity === 'watch');
  const [gap, setGap] = useState(false);
  const [quiz, setQuiz] = useState<QuizState>(newQuiz);
  const [tally, setTally] = useState<Tally>({ asked: 0, firstTry: 0 });
  /** Which showing of the guided demo (0 none yet); "Watch again" adds one. */
  const [watchRun, setWatchRun] = useState(activity === 'watch' ? 1 : 0);
  const shown =
    activity === 'quiz' && quiz.answered === null
      ? 'Which one makes a strong r: number 1 or number 2?'
      : caption;
  const watch = () => {
    setWatchRun((n) => n + 1);
  };

  // The guided demo, once per run.
  useEffect(() => {
    const s = scene.current;
    if (activity !== 'watch' || watchRun === 0 || !s) return;
    let alive = true;
    const stopped = () => !alive;
    const run = async () => {
      await s.ready;
      if (stopped()) return;
      setWatching(true);
      s.setBlend('rest', 'rest', 1);
      for (const step of WATCH) {
        if (stopped()) return;
        setCaption(step.caption);
        setGap(Boolean(step.gap));
        s.setGapMarker(Boolean(step.gap));
        const shot = s.currentShot === step.shot ? Promise.resolve() : s.setShot(step.shot, 1600);
        await Promise.all([
          s.moveTo(blendShapes(shapeOf('rest'), shapeOf(step.pose), step.amount), 1500),
          shot,
        ]);
        if (step.tour) await s.tour(6500);
        await wait(step.holdMs);
      }
      if (stopped()) return;
      s.setGapMarker(false);
      setGap(false);
      setCaption('Now you try: lift the middle of your tongue, keep the tip low, and say "rrr".');
      setWatching(false);
    };
    void run();
    return () => {
      alive = false;
    };
  }, [activity, scene, watchRun]);

  // The quiz shows its two shapes in turn until answered.
  useEffect(() => {
    if (activity !== 'quiz') return;
    const s = scene.current;
    if (!s || quiz.answered !== null) return;
    let alive = true;
    const loop = async () => {
      await s.ready;
      let showing: 0 | 1 = quiz.showing;
      while (alive) {
        setQuiz((q) => ({ ...q, showing }));
        await s.moveTo(quiz.shapes[showing], 1100);
        await wait(1800);
        await s.moveTo('rest', 700);
        showing = showing === 0 ? 1 : 0;
      }
    };
    void loop();
    return () => {
      alive = false;
    };
    // The quiz restarts only when a new one is dealt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, quiz.shapes, quiz.answered]);

  const answer = (pick: 0 | 1) => {
    const s = scene.current;
    if (!s || quiz.answered !== null) return;
    const right = quiz.shapes[pick] === 'rBunched';
    const r = quiz.shapes[0] === 'rBunched' ? 1 : 2;
    setTally((t) => ({ asked: t.asked + 1, firstTry: t.firstTry + (right ? 1 : 0) }));
    setQuiz((q) => ({ ...q, answered: pick }));
    void s.moveTo('rBunched', 900);
    setCaption(
      right
        ? `Yes! Number ${String(r)} is the strong r: the middle lifts close to the roof, the tip stays low.`
        : `${LOOKALIKES[quiz.lookalike].explain} The strong r is number ${String(r)}: see how the middle lifts.`,
    );
  };

  const more = () => {
    if (activity === 'watch' || tally.asked >= QUIZ_ROUNDS) {
      onDone(activity === 'watch' ? { asked: 0, firstTry: 0 } : tally);
      return;
    }
    setQuiz(newQuiz());
  };

  useHears(
    register,
    (text) => {
      if (activity === 'quiz' && quiz.answered === null) {
        const pick = matchChoice(text, QUIZ_PROMPT.options);
        if (pick) answer(pick === '1' ? 1 : 0);
        return pick !== null;
      }
      if (matchChoice(text, NEXT)) {
        more();
        return true;
      }
      if (
        activity === 'watch' &&
        !watching &&
        matchChoice(text, [{ id: 'again', says: ['again'] }])
      ) {
        watch();
        return true;
      }
      return false;
    },
    activity === 'quiz' && quiz.answered === null
      ? QUIZ_PROMPT
      : activity === 'watch' && !watching
        ? NEXT_PROMPT
        : null,
  );

  const onPick = (e: React.MouseEvent<HTMLButtonElement>) => {
    answer(e.currentTarget.dataset.pick === '1' ? 1 : 0);
  };

  return (
    <>
      <Stage>
        <div ref={host} className={styles.model} role="img" aria-label="The mouth from the side" />
        {gap && <GapKey />}
        {activity === 'quiz' && quiz.answered === null && (
          <span className={styles.badge} aria-live="polite">
            {quiz.showing + 1}
          </span>
        )}
      </Stage>
      <p className={styles.caption} aria-live="polite">
        {shown}
      </p>
      <div className={styles.actions}>
        {activity === 'watch' && !watching && (
          <>
            <button type="button" className={styles.button} onClick={watch}>
              Watch again
            </button>
            <button type="button" className={styles.button} onClick={more}>
              Next
            </button>
          </>
        )}
        {activity === 'quiz' &&
          (quiz.answered === null ? (
            ([0, 1] as const).map((i) => (
              <button
                key={i}
                type="button"
                className={styles.button}
                data-pick={i}
                onClick={onPick}
              >
                Number {i + 1}
              </button>
            ))
          ) : (
            <button type="button" className={styles.button} onClick={more}>
              {tally.asked >= QUIZ_ROUNDS ? 'Done' : 'Another one'}
            </button>
          ))}
      </div>
    </>
  );
}
